import React, { useState, useMemo, lazy, useEffect, useCallback, useRef } from 'react'
import { Layout, Menu, Dropdown, Breadcrumb, message } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, Link, useLocation, Outlet } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import styles from './index.module.scss'
import JarvisLogo from '@/assets/images/logo.jpg'
import classNames from 'classnames'
import SvgIcon from '@/components/svg-icon'
import { getBreadcrumbNameMap, getItem, getTreeMenu } from '@/utils/common'
import { closeMenu, openMenu } from '../../store/reducers/tab'
import Cookies from 'js-cookie'
import { AliveScope } from 'react-activation'
import logoutIcon from '@/assets/images/username-icon.jpg'
import switchIcon from '@/assets/images/logout-icon.jpg'
import overviewIcon from '@/assets/images/overview-icon.jpg'
import voiceIcon from '@/assets/images/voice-icon.jpg'
import { fetchUserProfile } from '@/service/common'
import AILogout from '@/pages/ailogout'
import { CustomerServiceOutlined } from '@ant-design/icons'
import { MENU_PERMISSION_MAP, getAllowedMenuKeys } from '@/constants/menuPermission'
import { buildAiccMenu, isDirectAiccRoute } from '@/next/aicc-navigation.mjs'
import { useNextPhone, type PhoneMode } from '@/next/PhoneProvider'

const { Header, Sider, Content } = Layout
// 提取底层路由方法
const getMenus = routes => {
  let menus = []
  function getMenuItem(route) {
    route.forEach(item => {
      if (item.children && item.children.length) getMenuItem(item.children)
      else {
        // 排除默认路由
        if (item.path) menus.push(item)
      }
    })
  }
  getMenuItem(routes)
  return menus
}
type LogoutHandler = () => void

let logoutHandler: LogoutHandler | null = null
const logoutListeners = new Set<LogoutHandler>()

export const onLogout = (listener: LogoutHandler) => {
  logoutListeners.add(listener)
  return () => {
    logoutListeners.delete(listener)
  }
}

const clearLocalSession = () => {
  // 清除认证相关的 cookies
  Cookies.remove('access_token')
  Cookies.remove('vue_admin_template_token')
  Cookies.remove('username')
  Cookies.remove('password')

  // 清除本地存储中的相关数据
  localStorage.removeItem('business_web_token')
  localStorage.removeItem('bot_list_should_refresh')
  // 注意：保留 current_username，用于下次登录时判断是否切换了账号

  // 清除会话存储
  sessionStorage.clear()

  // 跳转到登录页
  window.location.href = '/#/login'
}

export const registerLogoutHandler = (handler: LogoutHandler | null) => {
  logoutHandler = handler
}

//退出
export const logout = () => {
  logoutListeners.forEach((listener) => listener())
  if (logoutHandler) {
    logoutHandler()
    return
  }
  clearLocalSession()
}
const LayoutApp = () => {
  const phone = useNextPhone()
  const [showAILogout, setShowAILogout] = useState(false)
  const [pendingLogout, setPendingLogout] = useState(false)
  const logoutCompletedRef = useRef(false)

  const handleLogoutCompleted = useCallback(() => {
    if (logoutCompletedRef.current) return
    logoutCompletedRef.current = true
    setShowAILogout(false)
    setPendingLogout(false)
    clearLocalSession()
  }, [])

  const triggerLogout = useCallback(() => {
    logoutCompletedRef.current = false
    setPendingLogout(true)
    setShowAILogout(true)
  }, [])

  useEffect(() => {
    registerLogoutHandler(triggerLogout)

    return () => registerLogoutHandler(null)
  }, [triggerLogout])

  useEffect(() => {
    if (!pendingLogout) return

    const timeout = window.setTimeout(() => handleLogoutCompleted(), 5000)

    return () => window.clearTimeout(timeout)
  }, [pendingLogout, handleLogoutCompleted])

  const { t } = useTranslation()
  /** 通用hook */
  const dispatch = useDispatch()
  const navigate = useNavigate()
  // 侧边栏伸缩
  const collapsed = useSelector(state => state.tab.isCollapse)
  const userInfo = useSelector((state: any) => state.user.userInfo)
  const userMenus = userInfo?.menus as string[] | undefined
  const allowedKeys = useMemo(() => getAllowedMenuKeys(userMenus), [userMenus])
  /** 侧边栏菜单 */
  const { pathname } = useLocation()
  const permissionRoutes = useSelector(
    state => state.permission.permissionRoutes
  )
  //全屏
  const [isFullScreen, setIsFullScreen] = useState(false)
  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      // 进入全屏
      document.documentElement.requestFullscreen().then(() => {
        setIsFullScreen(true)
      })
    } else {
      // 退出全屏
      if (document.exitFullscreen) {
        document.exitFullscreen().then(() => {
          setIsFullScreen(false)
        })
      }
    }
  }
  const username = Cookies.get('username')
  // 获取当前路径数组片段
  const pathSnippets = pathname.split('/').filter(i => i)
  /** 面包屑 */
  const breadcrumbNameMap = useMemo(
    () => getBreadcrumbNameMap(permissionRoutes),
    [permissionRoutes]
  )
  const breadcrumbItems = pathSnippets.map((_, index) => {
    const url = `/${pathSnippets.slice(0, index + 1).join('/')}`
    // 如果是最后一项，即当前页面路由，渲染文本不可点击跳转
    if (index + 1 === pathSnippets.length)
      return {
        key: url,
        title: breadcrumbNameMap[url]
      }
    // 其余用link标签可点击跳转（注意：上级路由默认跳转到其定义的重定向路由，例如/system跳转至/system/user）
    return {
      key: url,
      title: <Link to={url}>{breadcrumbNameMap[url]}</Link>
    }
  })
  //菜单Items
  // const menuItems = useMemo(() => {
  //   return [
  //     getItem(
  //       <Link to="/botstudio">Home</Link>,
  //       '/flow',
  //       <SvgIcon name="home" color="#ffffff"></SvgIcon>
  //     )
  //   ].concat(getTreeMenu(permissionRoutes))
  // }, [permissionRoutes])
  const menuItems = useMemo(() => {
    return [
      getItem(
        t('Overview'),
        'overview',
        null,
        [
          getItem(
            t('Voice Bot'),
            '/dashboard', // 保持与路径一致
            <SvgIcon name="overview-vb" width="16" height="16" />,
            null
          ),
          getItem(
            t('Chat Bot'),
            '/chatbot-overview',
            <SvgIcon name="overview-cb" width="16" height="16" />,
            null
          ),

        ]
      ),
      getItem(
        t('BUILD'),
        'build',
        null,
        [
          ...getTreeMenu(permissionRoutes)
        ]
      ),
      getItem(
        t('CALL'),
        'call',
        null,
        [
          getItem(
            t('Batch Call'),
            '/batch-call', // 修改为与路径一致
            <SvgIcon name="batchcall" width="16" height="16" />,
          )
        ]
      ),
      getItem(
        t('CHAT'),
        'chat',
        null,
        [
          getItem(
            t('Channel'),
            '/channel',
            <SvgIcon name="channel" width="16" height="16" />,
          ),
          getItem(
            t('Live Chat'),
            '/live-chat',
            <SvgIcon name="Livechat" width="16" height="16" />,
          ),
          getItem(
            t('Broadcast'),
            '/broadcast',
            <SvgIcon name="broadcast" width="16" height="16" />,
          ),
        ]
      ),
      getItem(
        t('OBSERVE'),
        'observe',
        null,
        [
          getItem(
            t('Call Detail'),
            '/call-detail', // 修改为与路径一致
            <SvgIcon name="cd" width="16" height="16" />,
          ),
          getItem(
            t('Talk Detail'),
            '/webtalk-detail', // 修改为与路径一致
            <CustomerServiceOutlined width="16" height="16" />,
          ),
          getItem(
            t('Chat Detail'),
            '/chat-detail', // 修改为与路径一致
            <SvgIcon name="chatdetail" width="16" height="16" />,
          ),
        ]
      ),
      getItem(
        t('PAY'),
        'pay',
        null,
        [
          getItem(
            t('Billing'),
            '/bill', // 修改为与路径一致
            <SvgIcon name="bill" width="16" height="16" />,
          )
        ]
      )
      // getItem(
      //   t('VOICE'),
      //   'voice',
      //   null,
      //   [
      //     getItem(
      //       t('Voice Test'),
      //       '',
      //       // <img src={voiceIcon} className={styles.menuIcon} />,
      //       <SvgIcon name="voice-icon" width="18" height="18" />,
      //     )
      //   ]
      // ),
    ];
  }, [permissionRoutes, t]);

  const aiccMenuItem = useMemo(() => {
    if (process.env.LLMBOT_NEXT !== true) return null
    const menu = buildAiccMenu({
      menus: userMenus,
      pdsEnabled: userInfo?.pds_enabled,
      managementEnabled: userInfo?.aicc_management_enabled,
      collaborationRecordsEnabled: userInfo?.collaboration_record_read_enabled,
      operationsOverviewEnabled: userInfo?.operations_overview_enabled,
      principalType: userInfo?.principal_type,
      businessLines: userInfo?.business_lines,
      capabilities: userInfo?.capabilities,
      scopes: userInfo?.scopes,
    })
    if (!menu) return null
    return getItem(
      menu.label,
      menu.key,
      null,
      menu.children.map(item =>
        getItem(
          item.label,
          item.key,
          <SvgIcon name={item.icon} width="16" height="16" />
        )
      )
    )
  }, [userInfo?.pds_enabled, userInfo?.aicc_management_enabled, userInfo?.collaboration_record_read_enabled, userInfo?.operations_overview_enabled, userInfo?.principal_type, userInfo?.business_lines, userInfo?.capabilities, userInfo?.scopes, userMenus])

  const filteredMenuItems = useMemo(() => {
    const originalItems = !allowedKeys ? menuItems : menuItems
      .map((group: any) => {
        if (!group?.children) {
          return allowedKeys.has(group?.key) ? group : null
        }
        const filteredChildren = group.children.filter(
          (child: any) => allowedKeys.has(child?.key)
        )
        if (filteredChildren.length === 0) return null
        return { ...group, children: filteredChildren }
      })
      .filter(Boolean)
    return aiccMenuItem ? [...originalItems, aiccMenuItem] : originalItems
  }, [menuItems, allowedKeys, aiccMenuItem])
  //个人中心操作
  const operationItems = [
    {
      key: '1',
      label: (
        <a onClick={() => logout()} target="_blank" rel="noopener noreferrer">
          {t('layout.exit')}
        </a>
      )
    }
  ]
  // 格式化路由数组
  const Home = lazy(() => import('@/pages/home'))
  const formatRoutes = useMemo(() => {
    return [{ title: 'Home', menuPath: '/home', element: <Home /> }].concat(
      getMenus(permissionRoutes)
    )
  }, [permissionRoutes])
  const onMouseEnter = () => {
    dispatch(openMenu())
  }
  const onMouseLeave = () => {
    dispatch(closeMenu())
  }
  //openKeys,打开的keys
  //selectedKey,选中的keys
  const [openKeys, setOpenKeys] = useState([''])
  const [selectedKey, setSelectedKey] = useState([''])
  const onOpenChange = openKeys => {
    if (openKeys.length) {
      setOpenKeys([openKeys[openKeys.length - 1]])
    }
  }
  // 点击菜单
  const handleMenuClick = ({ key }) => {

    if (isDirectAiccRoute(key)) {
      navigate(key)
      return
    }

    if (key === '/aicc/pds/agent' || key === '/aicc/collaboration') {
      const mode: PhoneMode = key === '/aicc/pds/agent' ? 'auto' : 'collaboration'
      if (!phone.canSwitchTo(mode)) {
        message.warning(
          phone.status.activeCall || phone.status.agentState === 'After_Call_Work'
            ? 'Finish the current call and after-call work before switching agent workspaces.'
            : 'This account is not authorized for this agent workspace.'
        )
        return
      }
      navigate(key)
      return
    }

    // 处理dashboard路由
    if (key === '/dashboard') {
      navigate('/dashboard')
      return
    }

    if (key === '/chatbot-overview') {
      navigate('/chatbot-overview')
      return
    }

    if (key === '/batch-call') { // 修改为与key一致
      navigate('/batch-call')
      return
    }

    if (key === '/call-detail') {
      navigate('/call-detail')
      return
    }

    if (key === '/webtalk-detail') {
      navigate('/webtalk-detail')
      return
    }

    if (key === '/bill') {
      navigate('/bill')
      return
    }

    if (key === '/channel') {
      navigate('/channel')
      return
    }

    if (key === '/live-chat') {
      navigate('/live-chat')
      return
    }
    if (key === '/chat-detail') {
      navigate('/chat-detail')
      return
    }

    if (key === '/broadcast') {
      navigate('/broadcast')
      return
    }

    // 处理外部链接跳转
    if (key === 'batch-call-external') {
      window.open('https://aidocall.ai/ai/task/main', '_blank')
      return
    }

    // 菜单无子项，跳转
    if (formatRoutes.find(item => item.menuPath === key)) {
      navigate(key)
    }
  }
  useEffect(() => {
    if (pathname === '/') return
    setSelectedKey([pathname])
    let key = '/' + pathname.split('/')[1]
    setOpenKeys([key])
  }, [pathname])
  return (
    <Layout className={styles.appWrapper}>
      {showAILogout && (
        <AILogout onCompleted={handleLogoutCompleted} />
      )}
      <Sider
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        trigger={null}
        collapsed={collapsed}
        width={200}
        className={classNames([
          collapsed ? styles.hideSide : 'openSide',
          styles.app_side
        ])}
      >
        <h3 className={styles.app_name}>
          <img
            src={JarvisLogo}
            width={'90'}
            alt={"Logo"}
          />
          {/*<SvgIcon*/}
          {/*  name={collapsed ? 'logo1' : 'logo'}*/}
          {/*  width={collapsed ? '20' : '170'}*/}
          {/*  color="#ffffff"*/}
          {/*></SvgIcon>*/}
        </h3>
        <div className={styles.menuScrollContainer}>
          <Menu
            mode="inline"
            style={{
              borderRight: 0
            }}
            selectedKeys={selectedKey}
            openKeys={filteredMenuItems.map(item => item?.key)}
            onOpenChange={() => { }}
            items={filteredMenuItems}
            onClick={handleMenuClick}
            className={styles.app_menu}
          />
        </div>
        <div>
          <Dropdown menu={{ items: operationItems }}>
            <a className={styles.logout} onClick={e => e.preventDefault()}>
              <img
                src={logoutIcon}
                alt=""
                style={{
                  width: 16,
                  height: 16,
                }}
              />
              <span className={styles.username} > {username} </span>
              <img
                src={switchIcon}
                alt=""
                style={{
                  width: 16,
                  height: 20,
                }}
              />
            </a>
          </Dropdown>
        </div>
      </Sider>
      <Layout className={styles.siteLayout}>
        {/* <Header
          style={{
            padding: 0,
            display: 'flex',
            background: '#ffffff'
          }}
        >
          <div className={styles.headerContainer}>
            <Breadcrumb
              items={breadcrumbItems}
              style={{
                margin: '16px 0'
              }}
            />
            <div className={styles.headerRight}>
              <span className={styles.fullScreen} onClick={toggleFullScreen}>
                <SvgIcon
                  name={isFullScreen ? 'tuichuquanping' : 'quanping'}
                  width="20"
                  height="20"
                  color="#000000"
                ></SvgIcon>
              </span>
              <Dropdown menu={{ items: operationItems }}>
                <a className={styles.avatar} onClick={e => e.preventDefault()}>
                  <SvgIcon name="morentouxiang" color="#44c9c1"></SvgIcon>
                  <span>{usename}</span>
                </a>
              </Dropdown>
            </div>
          </div>
        </Header> */}
        <Content className={styles.contentScroll}>
          {/* <AliveScope> */}
          <Outlet />
          {/* </AliveScope> */}
        </Content>
      </Layout>
    </Layout>
  )
}
export default LayoutApp
